import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';
import {
  DsBannerState,
  DsButtonVariants,
  DsLoadingSpinnerSize,
  DsLoadingSpinnerVariant,
  DsModule,
} from '@levelaccess/design-system';
import { StatusCodes } from 'http-status-codes';
import { Subscription } from 'rxjs';

import {
  IOAuthConsentDecisionResponse,
  IOAuthConsentDetails,
  IOAuthConsentLoadResponse,
} from '@shared/interfaces/oauth.interface';
import { SharedCommonUtility } from '@shared/utils/common.utility';

import { SharedModule } from '../../../../shared/shared.module';
import { OAuthService } from '../../../../services/oauth.service';
import { LoginCardComponent } from '../../login/login-card/login-card.component';
import { isSafeNavigationUrl } from '../oauth-navigation.utility';

enum ConsentState {
  loading = 'loading',
  ready = 'ready',
  continuing = 'continuing',
  failed = 'failed',
}

const GENERIC_ERROR_KEY: string = 'oauth_consent_failed';

const ERROR_KEY_BY_STATUS: Partial<Record<number, string>> = {
  [StatusCodes.NOT_FOUND]: 'oauth_consent_not_found',
  [StatusCodes.FORBIDDEN]: 'oauth_consent_forbidden',
  [StatusCodes.CONFLICT]: 'oauth_consent_already_decided',
};

/**
 * Last step before an application is connected to Level Access: shown on the organization's own address, after the user
 * signed in there as usual (password and MFA, or the organization's single sign-on). The user decides whether to allow it.
 */
@Component({
  selector: 'app-oauth-consent',
  templateUrl: './oauth-consent.component.html',
  standalone: true,
  imports: [SharedModule, DsModule, LoginCardComponent],
})
export class OAuthConsentComponent implements OnInit, OnDestroy {
  protected readonly DsBannerState: typeof DsBannerState = DsBannerState;
  protected readonly DsButtonVariants: typeof DsButtonVariants = DsButtonVariants;
  protected readonly DsLoadingSpinnerSize: typeof DsLoadingSpinnerSize = DsLoadingSpinnerSize;
  protected readonly DsLoadingSpinnerVariant: typeof DsLoadingSpinnerVariant = DsLoadingSpinnerVariant;
  protected readonly ConsentState: typeof ConsentState = ConsentState;
  protected readonly docsUrl: string =
    'https://client.levelaccess.com/hc/en-us/articles/38378127264279-Tools-available-through-the-Level-Access-MCP-server';

  public state: ConsentState = ConsentState.loading;
  public details: IOAuthConsentDetails | null = null;
  public errorKey: string | null = null;
  /** The decision could not be sent, but the request is still open: the buttons stay, with this message above them. */
  public actionErrorKey: string | null = null;
  /** The decision is on its way: the buttons stay where they are, so that focus is not lost, and ignore clicks. */
  public isDeciding: boolean = false;

  @ViewChild('heading') private headingElement?: ElementRef<HTMLElement>;
  @ViewChild('actionError') private actionErrorElement?: ElementRef<HTMLElement>;

  private readonly requestId: string | undefined;
  private isFocusLostByRetry: boolean = false;
  private readonly subscriptions: Subscription = new Subscription();

  constructor(
    private oauthService: OAuthService,
    activatedRoute: ActivatedRoute,
  ) {
    this.requestId = activatedRoute.snapshot.queryParams['request'];
  }

  public ngOnInit(): void {
    this.load();
  }

  /** Whether trying again can help: the failure was not one of the definite answers (not found, forbidden, already answered). */
  public get canRetry(): boolean {
    return this.state === ConsentState.failed && this.errorKey === GENERIC_ERROR_KEY;
  }

  public retry(): void {
    this.state = ConsentState.loading;
    this.errorKey = null;
    // The button that was pressed is gone with the view of the failure: focus goes to the heading of the next view
    this.isFocusLostByRetry = true;
    this.load();
  }

  private load(): void {
    if (SharedCommonUtility.isNullishOrEmpty(this.requestId)) {
      this.fail(new HttpErrorResponse({ status: StatusCodes.NOT_FOUND }));
      return;
    }

    this.subscriptions.add(
      this.oauthService.getConsent(this.requestId).subscribe({
        next: (response: IOAuthConsentLoadResponse): void => {
          this.details = response.details;

          if (SharedCommonUtility.notNullishOrEmpty(response.completeUrl)) {
            // Approved before for this organization: no need to ask again
            this.continueTo(response.completeUrl);
            return;
          }
          this.state = ConsentState.ready;
          this.restoreFocusAfterRetry();
        },
        error: (error: HttpErrorResponse): void => this.fail(error),
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public allow(): void {
    this.decide(true);
  }

  public deny(): void {
    this.decide(false);
  }

  private decide(approve: boolean): void {
    if (this.state !== ConsentState.ready || this.isDeciding) {
      return;
    }

    this.isDeciding = true;
    this.actionErrorKey = null;
    this.subscriptions.add(
      this.oauthService.decideConsent(this.requestId, approve).subscribe({
        next: (response: IOAuthConsentDecisionResponse): void => this.continueTo(response.completeUrl),
        error: (error: HttpErrorResponse): void => {
          this.isDeciding = false;
          if (SharedCommonUtility.notNullish(ERROR_KEY_BY_STATUS[error.status])) {
            this.fail(error);
            return;
          }
          // A network problem or a server error says nothing about the request: let the user try again
          this.actionErrorKey = GENERIC_ERROR_KEY;
          this.focusAfterRender((): ElementRef<HTMLElement> | undefined => this.actionErrorElement);
        },
      }),
    );
  }

  private restoreFocusAfterRetry(): void {
    if (this.isFocusLostByRetry) {
      this.isFocusLostByRetry = false;
      this.focusAfterRender((): ElementRef<HTMLElement> | undefined => this.headingElement);
    }
  }

  /** The element is rendered by the change detection that follows, so it is looked up only then. */
  private focusAfterRender(element: () => ElementRef<HTMLElement> | undefined): void {
    setTimeout((): void => element()?.nativeElement.focus());
  }

  /**
   * A plain navigation back to the authorization server, which sends the browser (and the result) back to the application.
   * The address comes only from our own server, so the risk is low: the scheme check is defensive programming.
   */
  private continueTo(url: string): void {
    if (!isSafeNavigationUrl(url)) {
      this.fail(new HttpErrorResponse({ status: StatusCodes.BAD_GATEWAY }));
      return;
    }
    if (!this.isDeciding) {
      this.state = ConsentState.continuing;
    }
    window.open(url, '_self');
  }

  private fail(error: HttpErrorResponse): void {
    this.isDeciding = false;
    this.errorKey = ERROR_KEY_BY_STATUS[error.status] ?? GENERIC_ERROR_KEY;
    this.state = ConsentState.failed;
    this.restoreFocusAfterRetry();
  }
}
